import React, { ReactNode, useState } from "react";
import { Button, H1, H2, H3, Icon as ShoptetIcon, SystemMessage, Text, Tooltip } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import promoImage from "../../assets/finish_campaign_setup.png";
import video from "../../assets/loading-animation.mp4";
import { WIZARD_STEP_PATH } from "../../constants/paths";
import { Credits } from "../../elements/Credits";
import { CreditOptions, CreditTypes } from "../../elements/Credits/CreditOptions";
import { getCurrencyLimits, useCreditPayment } from "../../elements/Credits/useCreditPayment";
import { BackIcon, Icon as CustomIcon } from "../../elements/Icons";
import {
  StepperIconPropsT,
  VerticalCreditStepIcon,
  VerticalDoneStepIcon,
  VerticalSettingsStepIcon,
} from "../../elements/ProgressStepper/Icons";
import WizardHeader from "../../elements/WizardHeader";
import {
  AdSystemT,
  CreditsDocumentT,
  CurrencyT,
  MetaAdsWizardStepT,
  WizardStepT,
  useCreditsQuery,
  useLaunchRunwayQuery,
  useSetGoogleAdsWizardStepMutation,
  useSetMetaAdsWizardStepMutation,
  useStartSynchronizationMutation,
} from "../../graphql/generated/graphql";
import { useWizardState } from "../../hooks/useWizardState";
import { formatMoney } from "../../i18n/formatNumbers";
import { useAppSettings } from "../../providers/appSettingsProvider";
import { useSetWizardStepEffect } from "./useSetWizardStepEffect";

interface FinishProps {
  adSystem?: AdSystemT;
  hasMetaCampaignsEnabled: boolean;
}

type StepPropsT = {
  Icon: (props: StepperIconPropsT) => JSX.Element;
  children?: ReactNode;
  done?: boolean;
  hasNext?: boolean;
  header: string;
  text: string;
};

const Step = ({ Icon, children, done, hasNext, header, text }: StepPropsT) => {
  return (
    <div style={{ color: "var(--app-text)", display: "flex", flexDirection: "row", gap: "16px" }}>
      <div style={{ position: "relative" }}>
        {hasNext && (
          <div
            style={{
              position: "absolute",
              background: "var(--app-border)",
              width: "1px",
              height: "100%",
              left: "50%",
              top: 0,
            }}
          />
        )}
        <div
          style={{
            width: "44px",
            height: "44px",
            background: (done && "var(--app-brand-green)") || "var(--app-primary)",
            border: "10px solid var(--app-surface)",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "17px",
              width: "17px",
            }}
          >
            {done ? (
              <VerticalDoneStepIcon />
            ) : (
              <Icon color={done ? "var(--app-text-on-accent)" : "var(--app-text-subtle)"} />
            )}
          </div>
        </div>
      </div>
      <div>
        <h3 style={{ marginTop: "10px" }}>{header}</h3>
        <p>{text}</p>
        {children}
      </div>
    </div>
  );
};

export const FinishOld = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Wizard Finish Page" });
  const { data } = useCreditsQuery();
  const [startSynchronization] = useStartSynchronizationMutation({
    refetchQueries: [CreditsDocumentT],
    awaitRefetchQueries: true,
  });

  useSetWizardStepEffect({ wizardStep: WizardStepT.FinishT });
  const navigate = useNavigate();
  const zeroCredits = (data?.organization?.creditBalance?.amount || 0) <= 0;

  return (
    <div>
      <h1>{t("Headline")}</h1>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          flexDirection: "row",
          gap: "16px",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <video src={video} width="390" autoPlay loop playsInline />

        <div style={{ maxWidth: "463px", marginTop: "48px" }}>
          <Step header={t("step 1.header")} Icon={VerticalCreditStepIcon} text={t("step 1.text")} done hasNext />
          <Step
            done={!zeroCredits}
            header={t("step 2.header")}
            Icon={VerticalCreditStepIcon}
            text={t("step 2.text")}
            hasNext
          >
            <div className="v2form">
              <Credits fullWidth useRecommendedMonthlyBudget />
            </div>
          </Step>
          <Step header={t("step 3.header")} Icon={VerticalSettingsStepIcon} text={t("step 3.text")}>
            <div style={{ display: "inline-block", marginRight: "10px" }}>
              <Button data-test-id="back-to-check" variant="secondary" onClick={() => navigate("/wizard/check")}>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <BackIcon />
                  <span>&nbsp; {t("back")}</span>
                </span>
              </Button>
            </div>
            <Button
              data-test-id="start-campaign"
              disabled={zeroCredits}
              onClick={() =>
                startSynchronization({ variables: { adSystem: AdSystemT.GoogleAdsT } }).then(() =>
                  navigate("/dashboard")
                )
              }
            >
              {t("Start campaign")}
            </Button>
          </Step>
        </div>
      </div>
    </div>
  );
};

export const Finish = ({ adSystem = AdSystemT.GoogleAdsT, hasMetaCampaignsEnabled }: FinishProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "Wizard Finish Page" });
  const { t: tCredits } = useTranslation("translation", { keyPrefix: "Components.Credits" });
  const { currency: defaultCurrency } = useAppSettings();
  const { data, loading } = useCreditsQuery();
  const { data: runwayData, loading: runwayLoading } = useLaunchRunwayQuery({
    variables: { adSystem },
    fetchPolicy: "network-only",
  });
  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [isCreditValid, setIsCreditValid] = useState<boolean>(true);
  const [creditType, setCreditType] = useState<CreditTypes | null>(null);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [startSynchronization] = useStartSynchronizationMutation({
    refetchQueries: [CreditsDocumentT],
    awaitRefetchQueries: true,
  });
  const [setGoogleAdsWizardStep] = useSetGoogleAdsWizardStepMutation();
  const [setMetaAdsWizardStep] = useSetMetaAdsWizardStepMutation();

  const navigate = useNavigate();

  const { googleWizardFinished, metaWizardFinished } = useWizardState();
  const atLeastOneCampaignFinished = googleWizardFinished || metaWizardFinished;

  // Extract required data
  const currentCreditsAmount = data?.organization?.creditBalance?.amount || 0;
  const googleBudget = data?.organization?.googleAdsSetting?.monthlyBudget?.amount || 0;
  const metaBudget = data?.organization?.metaAdsSetting?.monthlyBudget?.amount || 0;
  const runningAdSystems = runwayData?.organization?.runningAdSystems || [];
  const googleRunning = runningAdSystems.includes(AdSystemT.GoogleAdsT);
  const metaRunning = runningAdSystems.includes(AdSystemT.MetaAdsT);
  const runningBudgetTotal = (googleRunning ? googleBudget : 0) + (metaRunning ? metaBudget : 0);

  const currency = defaultCurrency;
  const { maximalPaymentAmount } = getCurrencyLimits(currency);
  const recommendedBudget = data?.organization?.recommendedMonthlyBudget?.amount || 3000;

  let monthlyBudget = adSystem === AdSystemT.GoogleAdsT ? googleBudget : metaBudget;
  let monthlyBudgetThreeMonths = adSystem === AdSystemT.GoogleAdsT ? googleBudget * 3 : metaBudget * 3;
  let minimalNextPaymentAmount = recommendedBudget;

  if (atLeastOneCampaignFinished) {
    monthlyBudget = runningBudgetTotal;
    monthlyBudgetThreeMonths = runningBudgetTotal * 3;
    minimalNextPaymentAmount = currency === CurrencyT.CzkT ? 3000 : 120;
  }

  const calculatedRemainingCreditDays = runwayData?.organization?.remainingCreditDays || 0;
  const remainingDaysWord = t("day", { count: calculatedRemainingCreditDays });
  const isAllowedToProceedWithoutToppingCredit = currentCreditsAmount > recommendedBudget && atLeastOneCampaignFinished;

  const {
    error,
    initiatePaymentForAmount,
    isRedirecting,
    loading: loadingPayment,
  } = useCreditPayment({
    currency,
    startCampaignFor: adSystem,
  });

  const handleCreditChange = (selectedCreditType: CreditTypes, amount: number, isValid: boolean) => {
    setCreditAmount(amount);
    setIsCreditValid(isValid);
    setCreditType(selectedCreditType);
  };

  const handleStartCampaign = async () => {
    if (creditType === CreditTypes.PROCEED_WITHOUT_ADDING) {
      setIsNavigating(true);
      const setWizardStep =
        adSystem === AdSystemT.GoogleAdsT
          ? () => setGoogleAdsWizardStep({ variables: { wizardStep: WizardStepT.DashboardT } })
          : () => setMetaAdsWizardStep({ variables: { wizardStep: MetaAdsWizardStepT.DashboardT } });

      try {
        await setWizardStep();
        await startSynchronization({ variables: { adSystem } });
        navigate("/dashboard");
      } finally {
        setIsNavigating(false);
      }
    } else {
      initiatePaymentForAmount(creditAmount);
    }
  };

  const isButtonDisabled = loadingPayment || !isCreditValid || isNavigating;
  const isAdSystemGoogle = adSystem === AdSystemT.GoogleAdsT;

  useSetWizardStepEffect({
    adSystem,
    wizardStep: isAdSystemGoogle ? WizardStepT.FinishT : WizardStepT.MetaFinishT,
  });

  if (loading || runwayLoading || !data?.organization) {
    return <div>{t("Loading...")}</div>;
  }

  return (
    <>
      {hasMetaCampaignsEnabled && (
        <div>
          <WizardHeader />
          <H1>
            {/* One colour, same as the other wizard headlines - see MetaWizardHeader. */}
            {t("launch")} {isAdSystemGoogle ? t("Google campaign") : t("Meta campaign")}
          </H1>

          {error && (
            <div style={{ marginBottom: "16px" }}>
              <SystemMessage description={tCredits("Unable to initiate payment")} level="error" />
            </div>
          )}

          {isRedirecting && (
            <div style={{ justifyContent: "center", alignItems: "center", display: "flex", padding: "48px 0" }}>
              <div style={{ textAlign: "center" }}>
                <CustomIcon kind="loader" />
                <br />
                {tCredits("Redirection to payment gaytway in progress")}
              </div>
            </div>
          )}

          {!isRedirecting && (
            <>
              <div className="finish-campaign-container">
                <div className="finish-campaign-credits">
                  {!atLeastOneCampaignFinished && (
                    <>
                      <H2 divider={false}>{t("Headline")}</H2>
                      <Text>{t("For launching campaign you have to add credits")}</Text>
                    </>
                  )}
                  {atLeastOneCampaignFinished && (
                    <>
                      <H2 divider={false}>{t("Top up credit for Shoptet Campaigns")}</H2>
                      <div style={{ marginBottom: "24px" }}>
                        <Text>{t("To launch campaigns, you need to have a credit balance")}</Text>
                      </div>
                      {isAllowedToProceedWithoutToppingCredit && (
                        <SystemMessage
                          level="notice"
                          description={
                            <Trans
                              components={{ b: <b /> }}
                              i18nKey="You have credit info"
                              t={t}
                              values={{
                                credit: formatMoney({ amount: currentCreditsAmount, currency }),
                                remainingDays: calculatedRemainingCreditDays,
                                remainingDaysWord,
                              }}
                            />
                          }
                        />
                      )}
                      {!isAllowedToProceedWithoutToppingCredit && (
                        <SystemMessage
                          level="alert"
                          description={
                            <Trans
                              components={{ b: <b /> }}
                              i18nKey="You have credit alert"
                              t={t}
                              values={{
                                credit: formatMoney({ amount: currentCreditsAmount, currency }),
                                remainingDays: calculatedRemainingCreditDays,
                                remainingDaysWord,
                              }}
                            />
                          }
                        />
                      )}
                    </>
                  )}
                  {atLeastOneCampaignFinished && (
                    <>
                      {!isAllowedToProceedWithoutToppingCredit && (
                        <div style={{ marginTop: "24px" }}>
                          <Text appearance="strong-lg">{t("Top up your credit")}</Text>
                        </div>
                      )}
                      {isAllowedToProceedWithoutToppingCredit && (
                        <div style={{ marginTop: "24px" }}>
                          <Text appearance="strong-lg">{t("Do you want to top up with more credit")}</Text>
                        </div>
                      )}
                    </>
                  )}
                  <div style={{ marginTop: "24px" }}>
                    <CreditOptions
                      currency={currency}
                      isFirstPayment={false}
                      maximalPaymentAmount={maximalPaymentAmount}
                      minimalNextPaymentAmount={minimalNextPaymentAmount}
                      monthlyBudget={monthlyBudget}
                      monthlyBudgetThreeMonths={monthlyBudgetThreeMonths}
                      showProceedWithoutAdding={isAllowedToProceedWithoutToppingCredit}
                      onCreditChange={handleCreditChange}
                    />
                  </div>
                </div>
                <div
                  className="finish-campaign-optimization"
                  style={{ backgroundColor: "var(--app-info-surface)", borderRadius: "var(--app-radius-box)" }}
                >
                  {!atLeastOneCampaignFinished && (
                    <>
                      <div style={{ padding: "16px 16px 0" }}>
                        <H3 appearance="strong-lg">{t("How campaign is optimized in time")}</H3>
                        <div className="tooltip-as-link-container" style={{ marginBottom: "16px" }}>
                          <Text>
                            <Trans
                              components={{ tooltip: <Tooltip content={t("What is PNO tooltip")}> </Tooltip> }}
                              i18nKey="How campaign is optimized in time - description"
                              t={t}
                            />
                          </Text>
                        </div>
                        {/* Design system icons rather than flat assets, so they follow the theme instead of staying
                            dark on the dark background. `typography-no-margin` drops the `Text` bottom margin, so
                            the 8px between the rows is the whole gap and the label centres on its icon. */}
                        <div
                          className="typography-no-margin"
                          style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}
                        >
                          <ShoptetIcon icon="shp-money" size="sm" />
                          <Text>{t("Optimization feature 1")}</Text>
                        </div>
                        <div
                          className="typography-no-margin"
                          style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}
                        >
                          <ShoptetIcon icon="shp-ai" size="sm" />
                          <Text>{t("Optimization feature 2")}</Text>
                        </div>
                        <div
                          className="typography-no-margin"
                          style={{ display: "flex", alignItems: "center", gap: "12px" }}
                        >
                          <ShoptetIcon icon="shp-calendar-check" size="sm" />
                          <Text>{t("Optimization feature 3")}</Text>
                        </div>
                      </div>
                      <div style={{ padding: "70px 0 0" }}>
                        <img
                          alt="campaign optimization benefits"
                          src={promoImage}
                          style={{ maxWidth: "100%", marginTop: "-50px" }}
                        />
                      </div>
                    </>
                  )}
                  {atLeastOneCampaignFinished && (
                    <div style={{ padding: "16px" }}>
                      <div style={{ marginBottom: "16px" }}>
                        <Text appearance="strong-lg">{t("How is your advertising credit used")}</Text>
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <Trans components={{ b: <b /> }} i18nKey="You have one joint credit" t={t} />
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "8px",
                          backgroundColor: "var(--app-info-surface-inner)",
                          borderRadius: "var(--app-radius-inner)",
                          marginBottom: "16px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span style={{ color: "var(--app-primary)" }}>
                            <ShoptetIcon icon="shp-info" size="sm" />
                          </span>
                          <span>
                            <Text>{t("Your current credit status")}</Text>
                          </span>
                        </div>
                        <div>
                          <Text appearance="strong-md">{formatMoney({ amount: currentCreditsAmount, currency })}</Text>
                        </div>
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <Trans
                          components={{ b: <b /> }}
                          i18nKey="You set monthly budgets for individual advertising channels"
                          t={t}
                        />
                      </div>
                      {googleRunning && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingBottom: "8px",
                            marginBottom: "8px",
                            borderBottom: "1px solid var(--app-info-border)",
                          }}
                        >
                          <span>
                            <Text>{t("Monthly budget Google")}</Text>
                          </span>
                          <span>
                            <Text appearance="strong-md">{formatMoney({ amount: googleBudget, currency })}</Text>
                          </span>
                        </div>
                      )}
                      {metaRunning && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            paddingBottom: "8px",
                            marginBottom: "8px",
                            borderBottom: "1px solid var(--app-info-border)",
                          }}
                        >
                          <span>
                            <Text>{t("Monthly budget Meta")}</Text>
                          </span>
                          <span>
                            <Text appearance="strong-md">{formatMoney({ amount: metaBudget, currency })}</Text>
                          </span>
                        </div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>
                          <Text appearance="strong-md">
                            {t("The maximum amount of credit will be drawn per month")}
                          </Text>
                        </span>
                        <span>
                          <Text appearance="strong-md">{formatMoney({ amount: runningBudgetTotal, currency })}</Text>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px" }}>
                <Button
                  data-test-id="back-to-check"
                  variant="secondary"
                  onClick={() => navigate(isAdSystemGoogle ? WIZARD_STEP_PATH.CHECK : WIZARD_STEP_PATH.META_BUDGET)}
                >
                  {t("back")}
                </Button>
                <Button
                  data-test-id="start-campaign"
                  disabled={isButtonDisabled}
                  variant="action"
                  onClick={handleStartCampaign}
                >
                  {creditType === CreditTypes.PROCEED_WITHOUT_ADDING
                    ? t("Activate campaigns")
                    : t("Add credit and activate campaign")}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
      {!hasMetaCampaignsEnabled && <FinishOld />}
    </>
  );
};
